import type { FC, ReactNode } from "react";
import { useCallback, useEffect } from "react";

import dompurify from "isomorphic-dompurify";

import { useConfig } from "@jti/app-context/useConfig";
import { sortByStandaloneGifts } from "@jti/commerce/shared";
import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import { ICartItemErrorType } from "@jti/magento";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import { selectCartPending } from "@jti/store/selectors/selectCartPending";
import { selectFreeGiftsErrors } from "@jti/store/selectors/selectFreeGiftsErrors";
import { selectProductsInCart } from "@jti/store/selectors/selectProductsInCart";
import { selectQtyPending } from "@jti/store/selectors/selectQtyPending";
import {
  fetchCartPrices,
  fetchCleanCartItems,
  removeItem,
  removeItems,
  updateItemQuantity,
} from "@jti/store/slices/cartSlice";
import { updateTradeInOrderAttributes } from "@jti/store/slices/LEGACY__checkoutSlice";
import { useSetAdditionalAttributesMutation } from "@jti/store/slices/magentoApiSlice";
import { isRegularGift, isStandaloneGift } from "@jti/store/utils";
import { TradeInEligibilityBanner, useTranslations } from "@jti/ui";

import { CartItem } from "@checkout/components/CartItem";
import { BundleItem } from "@checkout/components/CartItem/BundleItem";
import { CartItemList } from "@checkout/components/CartItemList";
import { LoadingWrapper } from "@checkout/components/LoadingWrapper";
import { useGtm } from "@checkout/hooks/gtm/useGtm";
import { useFreeGifts } from "@checkout/hooks/useFreeGifts";

import { CartPromoGiftsContainer } from "../CartPromoGiftsContainer";

import { GiftBlock } from "./components";

import styles from "./CartItems.module.scss";

const sanitize = dompurify.sanitize;

type CartItemsProps = {
  warningStatus?: boolean;
  isLimitEnabled?: boolean;
  ageVerificationMessage?: ReactNode;
};

export const CartItems: FC<CartItemsProps> = ({ isLimitEnabled = false, warningStatus, ageVerificationMessage }) => {
  const { __ } = useTranslations();
  const config = useConfig();
  const dispatch = useDispatch();

  const cart = useSelector((state) => state.cart);
  const cartId = useSelector((state) => state.cart.id);
  const [setAdditionalAttributes] = useSetAdditionalAttributesMutation();
  const sortedProductsInCart = [
    ...useSelector((state) => selectProductsInCart(state, false, config.showPricesWithTaxExcluded)),
  ].sort(sortByStandaloneGifts);
  const freeTrialCart = useSelector((state) => state.freeTrialCart);
  const itemsPending = useSelector(selectCartPending);
  const qtyPending = useSelector(selectQtyPending);
  const freeGiftsErrors = useSelector(selectFreeGiftsErrors);
  const isStandaloneGiftNoLongerValid = freeGiftsErrors.some(
    (e) => e.errorCode === ICartItemErrorType.SalesruleGiftNotValid
  );

  const hasTradeInItems = sortedProductsInCart.some((item) => item.tradeIn);
  const shouldShowTradeInEligibilityBanner = Boolean(config.isTradeInEligibilityBannerEnabled && hasTradeInItems);

  const { dispatchGtmPageViewEvent, dispatchCartView } = useGtm();

  const { giftsByProduct, selectedFreeGifts, setSelectedFreeGiftQuantity } = useFreeGifts();

  useEffect(() => {
    if (cartId) {
      dispatch(fetchCleanCartItems(cartId));
    }
  }, [cartId, dispatch]);

  const handleQuantityChange = useCallback(
    async (itemId: string, quantity: number) => {
      if (!cartId) return;

      const item = sortedProductsInCart.find(({ id }) => id === itemId);
      if (!item) return;

      const giftItems = sortedProductsInCart.filter((product) => product.isGift && product.parentGiftId === item.id);

      for (const giftItem of giftItems) {
        if (giftItem.id) {
          await dispatch(removeItem({ cartId, item: giftItem }));
        }
      }

      const relatedGifts = selectedFreeGifts[item.id!];
      if (relatedGifts) {
        Object.keys(relatedGifts).forEach((giftSku) => {
          setSelectedFreeGiftQuantity({ parentGiftId: item.id!, giftSku, quantity: 0 });
        });
      }

      await dispatch(updateItemQuantity({ cartId, itemId, quantity }));
      await dispatch(fetchCleanCartItems(cartId));
      await dispatch(fetchCartPrices(cartId));

      if (item.tradeIn) {
        dispatch(updateTradeInOrderAttributes(null));
        setAdditionalAttributes({ cartId, additionalAttributes: [] });
      }
    },
    [cartId, sortedProductsInCart, selectedFreeGifts, dispatch, setSelectedFreeGiftQuantity, setAdditionalAttributes]
  );

  const handleRemoveItem = useCallback(
    async (itemId: string) => {
      if (!cartId) return;

      const cartItem = sortedProductsInCart.find(({ id }) => id === itemId);

      if (cartItem) {
        await dispatch(removeItem({ cartId, item: cartItem }));
      }

      dispatch(fetchCleanCartItems(cartId));
      dispatch(fetchCartPrices(cartId));

      if (cartItem?.tradeIn) {
        dispatch(updateTradeInOrderAttributes(null));
        setAdditionalAttributes({ cartId, additionalAttributes: [] });
      }
    },
    [cartId, dispatch, sortedProductsInCart, setAdditionalAttributes]
  );

  const handleRemoveItems = useCallback(
    async (itemIds: string[]) => {
      if (!cartId) return;

      await dispatch(removeItems({ cartId, itemIds }));
      dispatch(fetchCleanCartItems(cartId));
      dispatch(fetchCartPrices(cartId));

      const tradeInItem = sortedProductsInCart.find((item) => item.tradeIn);
      if (tradeInItem) {
        dispatch(updateTradeInOrderAttributes(null));
        setAdditionalAttributes({ cartId, additionalAttributes: [] });
      }
    },
    [cartId, dispatch, sortedProductsInCart, setAdditionalAttributes]
  );

  useEffect(() => {
    if (!itemsPending && !freeTrialCart?.order?.orderNumber) {
      dispatchCartView(cart || freeTrialCart);
      dispatchGtmPageViewEvent("cart");
    }
  }, [itemsPending]);

  return (
    <LoadingWrapper loading={itemsPending}>
      {ageVerificationMessage && <div className={styles.ageVerificationBlock}>{ageVerificationMessage}</div>}
      {shouldShowTradeInEligibilityBanner && (
        <div className={styles.alertBlock}>
          <TradeInEligibilityBanner
            title={config.tradeInEligibilityBannerTitle}
            description={config.tradeInEligibilityBannerDescription}
          />
        </div>
      )}
      {isStandaloneGiftNoLongerValid && (
        <div className={styles.alertBlock}>
          <AlertBanner
            tone="warning"
            richTitle={
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitize(__(`cart.free_gifts.errors.${ICartItemErrorType.SalesruleGiftNotValid}`)),
                }}
              ></span>
            }
          />
        </div>
      )}
      <CartItemList
        items={sortedProductsInCart}
        testId="regular-cart-list"
        emptyMessage={__("cart.items.na")}
        onRemoveItems={handleRemoveItems}
        renderItem={(item) => {
          if (isRegularGift(item)) return null;

          const productGifts = giftsByProduct.find((g) => g.id === item.id);
          const hasFreeGifts = !!productGifts?.freeGifts?.length;
          const hasMultipleProductsWithGifts = giftsByProduct.length > 1;

          const hasErrors = freeGiftsErrors.some(
            (freeGiftError) => freeGiftError.parentSku === item.sku && freeGiftError.parentId === item.id
          );

          const giftBlock =
            (hasFreeGifts || hasErrors) && cartId ? (
              <GiftBlock
                parentItemSku={item.sku}
                parentItemId={item.id!}
                limit={productGifts?.limit ?? 0}
                freeGifts={productGifts?.freeGifts ?? []}
                selectedFreeGifts={selectedFreeGifts}
                setSelectedFreeGiftQuantity={setSelectedFreeGiftQuantity}
                hasMultipleProductsWithGifts={hasMultipleProductsWithGifts}
              />
            ) : null;

          const cartItem =
            item.type_id === "bundle" && config.displayBundleSelectedItems ? (
              <BundleItem
                key={item.id}
                item={item}
                qtyPending={qtyPending}
                blockQuantityAdd={warningStatus}
                onQuantityChange={handleQuantityChange}
                onRemoveClick={handleRemoveItem}
              />
            ) : (
              <CartItem
                key={item.id}
                item={item}
                qtyPending={qtyPending}
                isLimitEnabled={isLimitEnabled}
                blockQuantityAdd={warningStatus}
                onQuantityChange={handleQuantityChange}
                onRemoveClick={handleRemoveItem}
                isRemovable={!isStandaloneGift(item)}
                isQuantityInputVisible={!isStandaloneGift(item)}
              />
            );

          return (
            <div key={item.id} className={styles.cartItemWrapper}>
              {cartItem}
              {giftBlock}
            </div>
          );
        }}
      />
      <CartPromoGiftsContainer />
    </LoadingWrapper>
  );
};
